import { CognitoUserPool, CognitoUserAttribute, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { AWS_CONFIG } from '../aws-config';

const poolData = {
    UserPoolId: AWS_CONFIG.userPoolId,
    ClientId: AWS_CONFIG.userPoolWebClientId
};

const userPool = new CognitoUserPool(poolData);

class AuthManager {
    constructor() {
        this.listeners = new Set();
        this.currentUser = null;
        this.loading = true;
        this.checkInitialSession();
    }

    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.currentUser); // immediate update
        return () => this.listeners.delete(listener);
    }

    notifyListeners() {
        this.listeners.forEach(listener => listener(this.currentUser));
    }

    checkInitialSession() {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser != null) {
            cognitoUser.getSession((err, session) => {
                if (err) {
                    try {
                        cognitoUser.signOut();
                    } catch (e) {
                        // ignore
                    }
                    this.currentUser = null;
                    this.loading = false;
                    this.notifyListeners();
                    return;
                }
                
                cognitoUser.getUserAttributes((err, attributes) => {
                    if (err) {
                        try {
                            cognitoUser.signOut();
                        } catch (e) {
                            // ignore
                        }
                        this.currentUser = null;
                    } else {
                        const attrs = {};
                        attributes.forEach(attr => {
                            attrs[attr.getName()] = attr.getValue();
                        });
                        this.currentUser = {
                            username: cognitoUser.getUsername(),
                            email: attrs.email,
                            name: attrs.name || 'User'
                        };
                    }
                    this.loading = false;
                    this.notifyListeners();
                });
            });
        } else {
            this.currentUser = null;
            this.loading = false;
            this.notifyListeners();
        }
    }

    async signUp(email, password, name) {
        return new Promise((resolve, reject) => {
            const attributeList = [];
            const dataEmail = { Name: 'email', Value: email };
            const dataName = { Name: 'name', Value: name };

            attributeList.push(new CognitoUserAttribute(dataEmail));
            attributeList.push(new CognitoUserAttribute(dataName));

            userPool.signUp(email, password, attributeList, null, (err, result) => {
                if (err) {
                    reject(err.message || JSON.stringify(err));
                    return;
                }
                const cognitoUser = result.user;
                resolve(cognitoUser);
            });
        });
    }

    async confirmSignUp(email, code) {
        return new Promise((resolve, reject) => {
            const userData = {
                Username: email,
                Pool: userPool
            };
            const cognitoUser = new CognitoUser(userData);

            cognitoUser.confirmRegistration(code, true, (err, result) => {
                if (err) {
                    reject(err.message || JSON.stringify(err));
                    return;
                }
                resolve(result);
            });
        });
    }

    async login(email, password) {
        return new Promise((resolve, reject) => {
            const authenticationDetails = new AuthenticationDetails({
                Username: email,
                Password: password
            });

            const userData = {
                Username: email,
                Pool: userPool
            };

            const cognitoUser = new CognitoUser(userData);

            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (result) => {
                    cognitoUser.getUserAttributes((err, attributes) => {
                        if (err) {
                            reject(err.message);
                            return;
                        }
                        const attrs = {};
                        attributes.forEach(attr => {
                            attrs[attr.getName()] = attr.getValue();
                        });
                        this.currentUser = {
                            username: cognitoUser.getUsername(),
                            email: attrs.email,
                            name: attrs.name || 'User'
                        };
                        this.notifyListeners();
                        resolve(this.currentUser);
                    });
                },
                onFailure: (err) => {
                    reject(err.message || JSON.stringify(err));
                }
            });
        });
    }

    async forgotPassword(email) {
        return new Promise((resolve, reject) => {
            const userData = { Username: email, Pool: userPool };
            const cognitoUser = new CognitoUser(userData);
            cognitoUser.forgotPassword({
                onSuccess: (data) => resolve(data),
                onFailure: (err) => reject(err.message || JSON.stringify(err)),
            });
        });
    }

    async confirmPasswordReset(email, code, newPassword) {
        return new Promise((resolve, reject) => {
            const userData = { Username: email, Pool: userPool };
            const cognitoUser = new CognitoUser(userData);
            cognitoUser.confirmPassword(code, newPassword, {
                onSuccess: () => resolve(),
                onFailure: (err) => reject(err.message || JSON.stringify(err)),
            });
        });
    }

    logout() {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser != null) {
            cognitoUser.signOut();
        }
        this.currentUser = null;
        this.notifyListeners();
    }
    
    getCurrentUser() {
        return this.currentUser;
    }
}

export const authManager = new AuthManager();
